import { Icon } from './Icons';
import React, { useState } from 'react';
import { auth, db } from '../firebase';
import { collection, addDoc, serverTimestamp } from 'firebase/firestore';
import { apiPost } from '../api';

function Card({ children, style = {} }) {
  return <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border2)', borderRadius: 'var(--radius-lg)', padding: '1.25rem 1.5rem', marginBottom: '1rem', ...style }}>{children}</div>;
}

function OptionBtn({ label, sublabel, selected, onClick, color = 'var(--primary)' }) {
  return (
    <button onClick={onClick} style={{
      padding: '.75rem 1rem', borderRadius: 'var(--radius)', border: '1px solid',
      borderColor: selected ? color : 'var(--border2)',
      background: selected ? color + '12' : 'var(--bg-elevated)',
      cursor: 'pointer', fontFamily: 'var(--font-body)', textAlign: 'left',
      transition: 'all .15s', width: '100%',
    }}>
      <div style={{ fontSize: '.85rem', fontWeight: 500, color: selected ? color : 'var(--text)' }}>{label}</div>
      {sublabel && <div style={{ fontSize: '.7rem', color: 'var(--text3)', marginTop: '.15rem', fontWeight: 300 }}>{sublabel}</div>}
    </button>
  );
}

function ExerciseRow({ exercise, index }) {
  const [showInfo, setShowInfo] = useState(false);
  return (
    <div style={{ background: 'var(--bg-elevated)', border: '1px solid var(--border2)', borderRadius: 'var(--radius)', marginBottom: '.5rem', overflow: 'hidden' }}>
      <div style={{ padding: '.85rem 1rem', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '1rem' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '.75rem', flex: 1 }}>
          <div style={{ width: 26, height: 26, borderRadius: '50%', background: 'rgba(0,174,239,0.12)', border: '1px solid rgba(0,174,239,0.25)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '.68rem', fontWeight: 700, color: 'var(--primary)', flexShrink: 0 }}>{index + 1}</div>
          <div>
            <div style={{ fontSize: '.88rem', fontWeight: 500, color: 'var(--text)' }}>{exercise.name}</div>
            <div style={{ fontSize: '.72rem', color: 'var(--primary)', marginTop: '.1rem' }}>{exercise.sets} sets × {exercise.reps}{exercise.rest ? ` · Rest ${exercise.rest}` : ''}</div>
          </div>
        </div>
        <div style={{ display: 'flex', align: 'center', gap: '.5rem', flexShrink: 0 }}>
          {exercise.tag && <span style={{ fontSize: '.65rem', padding: '.2rem .6rem', borderRadius: 20, background: 'var(--bg-card)', border: '1px solid var(--border2)', color: 'var(--text3)' }}>{exercise.tag}</span>}
          <button onClick={() => setShowInfo(v => !v)} style={{ background: 'none', border: 'none', color: 'var(--primary)', fontSize: '.7rem', cursor: 'pointer', fontFamily: 'var(--font-body)' }}>
            {showInfo ? '▲' : 'How?'}
          </button>
        </div>
      </div>
      {showInfo && (
        <div style={{ padding: '.75rem 1rem', borderTop: '1px solid var(--border2)', background: 'var(--bg-card)', fontSize: '.78rem', lineHeight: 1.65 }}>
          {exercise.coachingCue && <div style={{ color: 'var(--text2)', marginBottom: '.3rem' }}><strong style={{ color: 'var(--primary)' }}>Cue: </strong>{exercise.coachingCue}</div>}
          {exercise.muscles && <div style={{ color: 'var(--text3)', marginBottom: '.2rem' }}><strong style={{ color: 'var(--text)' }}>Muscles: </strong>{exercise.muscles}</div>}
          {exercise.tempo && <div style={{ color: 'var(--text3)' }}><strong style={{ color: 'var(--text)' }}>Tempo: </strong>{exercise.tempo}</div>}
        </div>
      )}
    </div>
  );
}

export default function GenerateWorkout({ trainingProfile, healthData }) {
  const [type, setType] = useState('');
  const [split, setSplit] = useState('');
  const [duration, setDuration] = useState('');
  const [generating, setGenerating] = useState(false);
  const [workout, setWorkout] = useState(null);
  const [error, setError] = useState('');
  const [saved, setSaved] = useState(false);

  const types = [
    { id: 'strength', label: 'Strength', sublabel: 'Heavy compound lifts, low reps, high intensity', color: '#f0a050' },
    { id: 'hypertrophy', label: 'Hypertrophy', sublabel: 'Muscle building, moderate weight, higher volume', color: 'var(--primary)' },
    { id: 'endurance', label: 'Endurance', sublabel: 'Higher reps, shorter rest, muscular stamina', color: '#22c55e' },
    { id: 'cardio', label: 'Cardio', sublabel: 'Intervals, circuits, conditioning work', color: '#a78bfa' },
  ];

  const splits = [
    { id: 'upper', label: 'Upper Body', sublabel: 'Chest, back, shoulders, arms' },
    { id: 'lower', label: 'Lower Body', sublabel: 'Quads, hamstrings, glutes, calves' },
    { id: 'push', label: 'Push', sublabel: 'Chest, shoulders, triceps' },
    { id: 'pull', label: 'Pull', sublabel: 'Back, rear delts, biceps' },
    { id: 'legs', label: 'Legs', sublabel: 'Full lower body focus' },
    { id: 'full_body', label: 'Full Body', sublabel: 'Balanced head-to-toe session' },
  ];

  const durations = [
    { id: '30', label: '30 min', sublabel: 'Focused and efficient' },
    { id: '45', label: '45 min', sublabel: 'Standard session' },
    { id: '60', label: '60 min', sublabel: 'Full workout' },
  ];

  const typeConfig = types.find(t => t.id === type);

  async function handleGenerate() {
    if (!type || !split || !duration) { setError('Please select a type, split and duration.'); return; }
    setGenerating(true);
    setError('');
    setWorkout(null);
    setSaved(false);

    let healthContext = '';
    if (healthData?.blood?.systolicBP && parseFloat(healthData.blood.systolicBP) >= 140) healthContext += 'Elevated BP — cap RPE at 8, avoid maximal isometrics and heavy overhead.\n';
    if (healthData?.mental?.stressScore && parseFloat(healthData.mental.stressScore) >= 70) healthContext += 'High stress score — reduce volume and intensity, prioritise technique.\n';
    if (healthData?.blood?.vitaminD && parseFloat(healthData.blood.vitaminD) < 30) healthContext += 'Low Vitamin D — note increased injury risk, include thorough warm-up.\n';

    const equipment = trainingProfile?.equipment || 'Commercial gym (full)';
    const injuries = trainingProfile?.injuries || 'None';
    const experience = trainingProfile?.experience || 'Intermediate';
    const goal = trainingProfile?.goal || 'General fitness';

    const splitLabel = splits.find(s => s.id === split)?.label || split;
    const typeLabel = types.find(t => t.id === type)?.label || type;

    const prompt = `You are an elite personal trainer. Generate a single ${duration}-minute ${typeLabel} workout targeting ${splitLabel}.

ATHLETE:
Experience: ${experience}
Goal: ${goal}
Equipment: ${equipment}
Injuries: ${injuries}
${healthContext ? 'HEALTH FLAGS:\n' + healthContext : ''}

WORKOUT TYPE: ${typeLabel}
${type === 'strength' ? 'Use heavy compound lifts. 3-5 sets, 3-6 reps, RPE 8-9. Long rest periods (2-3 min). Include working weight guidance.' : ''}
${type === 'hypertrophy' ? 'Use moderate weights. 3-4 sets, 8-12 reps, RPE 7-8. 60-90 second rests. Include time under tension notes.' : ''}
${type === 'endurance' ? 'Higher reps (15-20+), lighter weights, shorter rests (30-45s). Superset where appropriate.' : ''}
${type === 'cardio' ? 'Interval-based or circuit format. Mix of cardio movements, bodyweight and light resistance. Keep heart rate elevated.' : ''}

SPLIT: ${splitLabel}

RULES:
- MUST include a warm-up (5 minutes) appropriate for the split
- MUST include at least 2 core exercises regardless of split
- MUST include a cool-down with static stretches (3-5 minutes)
- Total session should fit within ${duration} minutes including warm-up and cool-down
- For strength and hypertrophy, include cardio finisher (5-10 min) if time allows
- Include coaching cues for every exercise
- Make it genuinely excellent — like a session from a top PT

Return ONLY valid JSON, no markdown:
{
  "workoutTitle": "string",
  "type": "${type}",
  "split": "${split}",
  "duration": "${duration} min",
  "warmup": {
    "description": "string",
    "exercises": ["exercise 1", "exercise 2", "exercise 3"]
  },
  "mainBlock": [
    {
      "name": "Exercise name",
      "sets": 4,
      "reps": "8-10",
      "rest": "90 seconds",
      "tag": "Compound",
      "muscles": "Primary muscles",
      "coachingCue": "Key cue for this exercise",
      "tempo": "3-1-1-0"
    }
  ],
  "coreBlock": [
    {
      "name": "Core exercise",
      "sets": 3,
      "reps": "12 reps / 45 seconds",
      "rest": "30 seconds",
      "tag": "Core",
      "muscles": "Core muscles targeted",
      "coachingCue": "Key cue"
    }
  ],
  "cardioFinisher": {
    "description": "string",
    "exercises": ["exercise with duration or reps"]
  },
  "cooldown": {
    "description": "string",
    "stretches": ["stretch 1 — hold 30s", "stretch 2 — hold 30s"]
  },
  "coachNote": "One sentence motivation or key focus for this session",
  "estimatedCalories": "350-450 kcal"
}`;

    try {
      const data = await apiPost('/api/pt', {
        messages: [{ role: 'user', content: prompt }],
        trainingProfile, liftLog: '', workingWeights: '', healthData
      });
      const content = data.content || '';
      const jsonMatch = content.match(/\{[\s\S]*\}/);
      if (jsonMatch) {
        setWorkout(JSON.parse(jsonMatch[0]));
      } else {
        setError('Could not parse workout. Please try again.');
      }
    } catch (e) {
      setError('Generation failed. Please try again.');
    }
    setGenerating(false);
  }

  async function handleSaveToLog() {
    if (!workout) return;
    try {
      const user = auth.currentUser;
      if (!user) return;
      const allExercises = [...(workout.mainBlock || []), ...(workout.coreBlock || [])];
      await addDoc(collection(db, 'users', user.uid, 'history', 'training', 'entries'), {
        date: new Date().toISOString().split('T')[0],
        sessionName: workout.workoutTitle || 'Generated Workout',
        week: 0, block: 0,
        exercises: allExercises.map(ex => ({ name: ex.name, sets: ex.sets, reps: ex.reps, weight: '', rpe: '' })),
        fromGenerated: true,
        recordedAt: serverTimestamp()
      });
      setSaved(true);
    } catch (e) { console.error(e); }
  }

  return (
    <div style={{ maxWidth: 860, margin: '0 auto' }}>
      <div style={{ marginBottom: '1.75rem' }}>
        <h2 style={{ fontFamily: 'var(--font-head)', fontSize: '1.4rem', fontWeight: 600, color: 'var(--text)', marginBottom: '.3rem' }}>Generate a Workout</h2>
        <p style={{ fontSize: '.78rem', color: 'var(--text3)', fontWeight: 300 }}>Set your preferences and Rico PT will build a complete session — including core work and cardio — in seconds.</p>
      </div>

      {/* Selectors */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '1rem', marginBottom: '1rem' }}>
        <Card>
          <div style={{ fontSize: '.65rem', fontWeight: 600, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '.1em', marginBottom: '.85rem' }}>Training Type</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '.4rem' }}>
            {types.map(t => <OptionBtn key={t.id} label={t.label} sublabel={t.sublabel} selected={type === t.id} onClick={() => setType(t.id)} color={t.color} />)}
          </div>
        </Card>

        <div>
          <Card>
            <div style={{ fontSize: '.65rem', fontWeight: 600, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '.1em', marginBottom: '.85rem' }}>Split</div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '.4rem' }}>
              {splits.map(s => <OptionBtn key={s.id} label={s.label} sublabel={s.sublabel} selected={split === s.id} onClick={() => setSplit(s.id)} />)}
            </div>
          </Card>

          <Card>
            <div style={{ fontSize: '.65rem', fontWeight: 600, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '.1em', marginBottom: '.85rem' }}>Duration</div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '.4rem' }}>
              {durations.map(d => <OptionBtn key={d.id} label={d.label} sublabel={d.sublabel} selected={duration === d.id} onClick={() => setDuration(d.id)} />)}
            </div>
          </Card>
        </div>
      </div>

      {error && <div style={{ padding: '.75rem 1rem', background: 'var(--danger-dim)', border: '1px solid rgba(240,80,80,.2)', borderRadius: 'var(--radius)', color: 'var(--danger)', fontSize: '.78rem', marginBottom: '1rem' }}>{error}</div>}

      <button onClick={handleGenerate} disabled={generating || !type || !split || !duration} style={{
        width: '100%', padding: '.9rem', borderRadius: 'var(--radius-lg)', border: 'none',
        background: generating || !type || !split || !duration ? 'var(--bg-elevated)' : typeConfig ? typeConfig.color : 'var(--primary)',
        color: generating || !type || !split || !duration ? 'var(--text3)' : '#080f18',
        fontFamily: 'var(--font-body)', fontSize: '.95rem', fontWeight: 600,
        cursor: generating || !type || !split || !duration ? 'not-allowed' : 'pointer',
        marginBottom: '1.5rem', transition: 'all .2s'
      }}>
        {generating ? 'Rico PT is building your workout…' : 'Generate My Workout →'}
      </button>

      {generating && (
        <div style={{ textAlign: 'center', padding: '1rem 0' }}>
          <div style={{ width: 36, height: 36, borderRadius: '50%', border: '3px solid rgba(0,174,239,0.2)', borderTop: '3px solid var(--primary)', animation: 'spin .8s linear infinite', margin: '0 auto' }} />
          <style>{'@keyframes spin { to { transform: rotate(360deg); } }'}</style>
        </div>
      )}

      {/* Generated workout */}
      {workout && (
        <div style={{ animation: 'fadeIn .4s ease' }}>
          <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: '1.25rem', flexWrap: 'wrap', gap: '.75rem' }}>
            <div>
              <h3 style={{ fontFamily: 'var(--font-head)', fontSize: '1.3rem', fontWeight: 500, color: 'var(--text)', marginBottom: '.2rem' }}>{workout.workoutTitle || 'Generated Workout'}</h3>
              <div style={{ display: 'flex', gap: '.5rem', flexWrap: 'wrap' }}>
                {[workout.type, workout.split, workout.duration, workout.estimatedCalories].filter(Boolean).map((tag, i) => (
                  <span key={i} style={{ fontSize: '.7rem', padding: '.2rem .65rem', borderRadius: 20, background: 'var(--bg-elevated)', border: '1px solid var(--border2)', color: 'var(--text3)' }}>{tag}</span>
                ))}
              </div>
            </div>
            <div style={{ display: 'flex', gap: '.5rem' }}>
              <button onClick={handleGenerate} style={{ padding: '.5rem 1rem', borderRadius: 'var(--radius)', border: '1px solid var(--border2)', background: 'transparent', color: 'var(--text3)', fontFamily: 'var(--font-body)', fontSize: '.78rem', cursor: 'pointer' }}>↻ Regenerate</button>
              <button onClick={handleSaveToLog} disabled={saved} style={{ padding: '.5rem 1rem', borderRadius: 'var(--radius)', border: 'none', background: saved ? 'var(--success)' : 'var(--primary)', color: '#080f18', fontFamily: 'var(--font-body)', fontSize: '.78rem', fontWeight: 600, cursor: saved ? 'default' : 'pointer' }}>
                {saved ? '✓ Saved to history' : '+ Save to history'}
              </button>
            </div>
          </div>

          {workout.coachNote && (
            <div style={{ padding: '.85rem 1.1rem', background: 'rgba(240,160,80,.05)', border: '1px solid rgba(240,160,80,.2)', borderRadius: 'var(--radius-lg)', marginBottom: '1rem', fontSize: '.8rem', color: 'var(--warning)', lineHeight: 1.6 }}>
              <Icon name="message" size={14} /> <strong>Rico PT: </strong>{workout.coachNote}
            </div>
          )}

          {/* Warm up */}
          {workout.warmup && (
            <Card style={{ background: 'rgba(240,160,80,.04)', borderColor: 'rgba(240,160,80,.2)' }}>
              <div style={{ fontSize: '.65rem', fontWeight: 600, color: 'var(--warning)', textTransform: 'uppercase', letterSpacing: '.1em', marginBottom: '.6rem' }}>Warm-up · 5 min</div>
              <p style={{ fontSize: '.78rem', color: 'var(--text3)', marginBottom: '.6rem', fontWeight: 300 }}>{workout.warmup.description}</p>
              {(workout.warmup.exercises || []).map((ex, i) => (
                <div key={i} style={{ fontSize: '.8rem', color: 'var(--text2)', padding: '.25rem 0', borderBottom: '1px solid var(--border2)' }}>• {ex}</div>
              ))}
            </Card>
          )}

          {/* Main block */}
          {workout.mainBlock && workout.mainBlock.length > 0 && (
            <div style={{ marginBottom: '1rem' }}>
              <div style={{ fontSize: '.65rem', fontWeight: 600, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '.1em', marginBottom: '.75rem' }}>Main Block</div>
              {workout.mainBlock.map((ex, i) => <ExerciseRow key={i} exercise={ex} index={i} />)}
            </div>
          )}

          {/* Core block */}
          {workout.coreBlock && workout.coreBlock.length > 0 && (
            <div style={{ marginBottom: '1rem' }}>
              <div style={{ fontSize: '.65rem', fontWeight: 600, color: '#a78bfa', textTransform: 'uppercase', letterSpacing: '.1em', marginBottom: '.75rem' }}>Core Block</div>
              {workout.coreBlock.map((ex, i) => <ExerciseRow key={i} exercise={ex} index={i} />)}
            </div>
          )}

          {/* Cardio finisher */}
          {workout.cardioFinisher && workout.cardioFinisher.exercises && (
            <Card style={{ background: 'rgba(34,197,94,.04)', borderColor: 'rgba(34,197,94,.2)' }}>
              <div style={{ fontSize: '.65rem', fontWeight: 600, color: 'var(--success)', textTransform: 'uppercase', letterSpacing: '.1em', marginBottom: '.6rem' }}>Cardio Finisher</div>
              <p style={{ fontSize: '.78rem', color: 'var(--text3)', marginBottom: '.6rem', fontWeight: 300 }}>{workout.cardioFinisher.description}</p>
              {workout.cardioFinisher.exercises.map((ex, i) => (
                <div key={i} style={{ fontSize: '.8rem', color: 'var(--text2)', padding: '.25rem 0', borderBottom: '1px solid var(--border2)' }}>• {ex}</div>
              ))}
            </Card>
          )}

          {/* Cool down */}
          {workout.cooldown && (
            <Card style={{ background: 'rgba(0,174,239,.04)', borderColor: 'rgba(0,174,239,.15)' }}>
              <div style={{ fontSize: '.65rem', fontWeight: 600, color: 'var(--primary)', textTransform: 'uppercase', letterSpacing: '.1em', marginBottom: '.6rem' }}>Cool-down</div>
              <p style={{ fontSize: '.78rem', color: 'var(--text3)', marginBottom: '.6rem', fontWeight: 300 }}>{workout.cooldown.description}</p>
              {(workout.cooldown.stretches || []).map((s, i) => (
                <div key={i} style={{ fontSize: '.8rem', color: 'var(--text2)', padding: '.25rem 0', borderBottom: '1px solid var(--border2)' }}>• {s}</div>
              ))}
            </Card>
          )}

          <style>{'@keyframes fadeIn { from{opacity:0;transform:translateY(8px)} to{opacity:1;transform:translateY(0)} }'}</style>
        </div>
      )}
    </div>
  );
}
